import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Upload, Image, Settings, Home, User, Images, CreditCard, Lock, AlertCircle, Menu, Loader2, FlaskConical } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useSecureAuthContext } from "@/contexts/SecureAuthContext";
import AllGeneratedImages from "@/components/AllGeneratedImages";
import { useState, useEffect, useMemo, lazy, Suspense } from "react";
import { NavBar } from "@/components/ui/tubelight-navbar";
import { OptimizedDashboardStatsGrid } from "@/components/OptimizedDashboardStatsGrid";
import { useUnifiedUserData } from "@/hooks/useUnifiedUserData";
import PlanUpgradeModal from "@/components/PlanUpgradeModal";
import { toast } from "sonner";
import QueueMonitor from "@/components/QueueMonitor";
import CustomerReadinessNotice from "@/components/CustomerReadinessNotice";
import ClaimedImagesModal from "@/components/ClaimedImagesModal";
import { useGuestImageGeneration } from "@/hooks/useGuestImageGeneration";

// Lazy load heavy components for better performance
const ImageGeneration = lazy(() => import("@/components/ImageGeneration"));
const BatchUpload = lazy(() => import("@/components/BatchUpload"));
const Dashboard = () => {
  const {
    user,
    signOut
  } = useSecureAuthContext();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('generate');
  const [copiedSettings, setCopiedSettings] = useState<any>(null);
  const [mousePosition, setMousePosition] = useState({
    x: 0,
    y: 0
  });
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeContext, setUpgradeContext] = useState<any>(null);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const {
    profile: userData,
    loading: userDataLoading
  } = useUnifiedUserData();
  const { hasGuestImages, claimImages } = useGuestImageGeneration();
  const [claimedImages, setClaimedImages] = useState<any[]>([]);
  const [showClaimedModal, setShowClaimedModal] = useState(false);

  // Auto-claim guest images immediately after login (no delay)
  useEffect(() => {
    const claimGuestImages = async () => {
      if (user) {
        const result = await claimImages(user.id);
        if (result.count > 0 && result.images.length > 0) {
          setClaimedImages(result.images);
          setShowClaimedModal(true);
          toast.success(`Successfully claimed ${result.count} images from your guest session!`);
        }
      }
    };
    claimGuestImages();
  }, [user, claimImages]);

  // Mouse tracking for grid effect (desktop only) - Debounced for performance
  useEffect(() => {
    if (window.innerWidth < 768) return; // Skip on mobile entirely
    
    let rafId: number;
    const handleMouseMove = (e: MouseEvent) => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        setMousePosition({
          x: e.clientX,
          y: e.clientY
        });
      });
    };
    
    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);
  const handleSignOut = async () => {
    await signOut();
  };
  const handleCopySettings = (settings: any) => {
    setCopiedSettings(settings);
    setActiveTab('generate');
  };
  const handleHomeNavigation = () => {
    navigate('/');
  };
  const handleUpgradePlan = () => {
    navigate('/settings');
    toast.info('Upgrade your plan to access more features and credits');
  };
  const showUpgradeDialog = (feature: string, requiredPlans: string[]) => {
    setUpgradeContext({
      feature,
      requiredPlans,
      currentPlan: userData?.plan_type || 'free'
    });
    setShowUpgradeModal(true);
  };

  // Enhanced validation with unified data
  const canGenerateImages = () => {
    if (!userData) return false;

    // For pay-as-go plans, always allow (they pay per use)
    if (userData.plan_type === 'pay_as_go') return true;

    // For other plans, check credits and monthly limits
    const hasCredits = userData.credits_remaining > 0;
    const withinMonthlyLimit = userData.image_generations_used < userData.monthly_limit;
    const hasUnlimitedPlan = userData.monthly_limit === 999999;
    return hasCredits && (withinMonthlyLimit || hasUnlimitedPlan);
  };
  const canUseBatchUpload = () => {
    if (!userData) return false;
    const batchFeatures = {
      'starter': true,
      'professional': true,
      'business': true,
      'pay_as_go': true,
      'enterprise': true
    };
    return batchFeatures[userData.plan_type as keyof typeof batchFeatures] && userData.credits_remaining > 0;
  };
  const navItems = [{
    id: 'generate',
    name: 'Generate Images',
    icon: !canGenerateImages() ? Lock : Image,
    description: canGenerateImages() ? 'Create AI images' : userData?.plan_type === 'pay_as_go' ? 'Pay per image' : 'No credits/limit reached',
    disabled: !canGenerateImages()
  }, {
    id: 'batch',
    name: 'Batch Upload',
    icon: canUseBatchUpload() ? Upload : Lock,
    description: canUseBatchUpload() ? 'Bulk processing' : 'Starter+ required',
    disabled: !canUseBatchUpload()
  }, {
    id: 'abtesting',
    name: 'A/B Testing',
    icon: FlaskConical,
    description: 'Compare creative variations',
    isExternal: true,
    path: '/ab-testing'
  }, {
    id: 'queue',
    name: 'Queue Monitor',
    icon: Settings,
    description: 'Monitor generation progress'
  }, {
    id: 'gallery',
    name: 'Complete Gallery',
    icon: Images,
    description: 'View all images'
  }];
  const tabContent = useMemo(() => {
    try {
      if (activeTab === 'generate') {
        if (!canGenerateImages()) {
          return <div className="text-center py-6 sm:py-8 md:py-12">
              <Lock className="w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 text-gray-400 mx-auto mb-2 sm:mb-3 md:mb-4" />
              <h3 className="text-sm sm:text-base md:text-lg font-semibold text-white mb-2">Image Generation Restricted</h3>
              
              {userData && <div className="space-y-1 sm:space-y-2 text-[10px] sm:text-xs text-gray-400 mb-3 sm:mb-4 md:mb-6 px-2">
                  <p>Current Plan: <span className="text-white font-medium">{getPlanName(userData.plan_type)}</span></p>
                  <p>Monthly Limit: {userData.monthly_limit === 999999 ? 'Unlimited' : userData.monthly_limit} images</p>
                  <p>Used This Month: {userData.image_generations_used} images</p>
                  <p>Credits Remaining: {userData.credits_remaining}</p>
                  
                  {userData.plan_type !== 'pay_as_go' && userData.credits_remaining <= 0 && <p className="text-red-400 font-medium">⚠️ No credits remaining</p>}
                  
                  {userData.image_generations_used >= userData.monthly_limit && userData.monthly_limit !== 999999 && <p className="text-red-400 font-medium">⚠️ Monthly limit reached</p>}
                </div>}
              
              <Button onClick={() => showUpgradeDialog('image_generation', ['starter', 'professional', 'business'])} className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 h-9 sm:h-10 px-3 sm:px-4 text-xs">
                <CreditCard className="w-4 h-4 mr-2" />
                Upgrade Plan
              </Button>
            </div>;
        }
        return <Suspense fallback={
          <div className="text-center py-12">
            <Loader2 className="w-12 h-12 mx-auto mb-4 animate-spin text-blue-400" />
            <p className="text-white">Loading image generator...</p>
          </div>
        }>
          <ImageGeneration copiedSettings={copiedSettings} />
        </Suspense>;
      }
      if (activeTab === 'batch') {
        if (!canUseBatchUpload()) {
          return <div className="text-center py-6 sm:py-8 md:py-12">
              <Lock className="w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 text-gray-400 mx-auto mb-2 sm:mb-3 md:mb-4" />
              <h3 className="text-sm sm:text-base md:text-lg font-semibold text-white mb-2">Batch Upload Locked</h3>
              <p className="text-gray-300 mb-2 sm:mb-3 md:mb-4 text-[10px] sm:text-xs px-2">Batch upload requires Starter plan or higher</p>
              
              {userData && <div className="space-y-1 sm:space-y-2 text-[10px] sm:text-xs text-gray-400 mb-3 sm:mb-4 md:mb-6 px-2">
                  <p>Current Plan: <span className="text-white font-medium">{getPlanName(userData.plan_type)}</span></p>
                  <p>Credits Remaining: {userData.credits_remaining}</p>
                  <p>Monthly Usage: {userData.image_generations_used}/{userData.monthly_limit === 999999 ? 'Unlimited' : userData.monthly_limit}</p>
                </div>}
              
              <Button onClick={() => showUpgradeDialog('batch_upload', ['starter', 'professional', 'business'])} className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 h-9 sm:h-10 px-3 sm:px-4 text-xs">
                <CreditCard className="w-4 h-4 mr-2" />
                Upgrade to Starter
              </Button>
            </div>;
        }
        return <Suspense fallback={
          <div className="text-center py-12">
            <Loader2 className="w-12 h-12 mx-auto mb-4 animate-spin text-blue-400" />
            <p className="text-white">Loading batch uploader...</p>
          </div>
        }>
          <BatchUpload />
        </Suspense>;
      }
      if (activeTab === 'queue') {
        return <QueueMonitor />;
      }
      if (activeTab === 'gallery') {
        return <AllGeneratedImages onCopySettings={handleCopySettings} />;
      }
      return <div className="text-white">Invalid tab</div>;
    } catch (error) {
      console.error('Error rendering tab content:', error);
      return <div className="text-red-400 p-3 sm:p-4 md:p-6">
          <AlertCircle className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 mx-auto mb-2 sm:mb-3 md:mb-4" />
          <p className="text-center text-[10px] sm:text-xs">Error loading content. Please try again.</p>
        </div>;
    }
  }, [activeTab, copiedSettings, userData, canGenerateImages, canUseBatchUpload]);
  const handleTabChange = (tabId: string) => {
    const navItem = navItems.find(item => item.id === tabId);
    
    // Handle external links
    if (navItem?.isExternal && navItem.path) {
      navigate(navItem.path);
      return;
    }
    
    if (navItem?.disabled) {
      if (tabId === 'generate') {
        const reason = !canGenerateImages() ? 'Image generation not available - check your credits and monthly limits' : 'Image generation not available';
        toast.warning(reason);
      } else if (tabId === 'batch') {
        toast.warning('Batch upload requires Starter plan or higher');
      }
      return;
    }
    setActiveTab(tabId);
    setShowMobileMenu(false); // Close mobile menu after selection
  };
  const getPlanBadgeColor = (planType: string) => {
    switch (planType) {
      case 'free':
        return 'bg-gray-600';
      case 'starter':
        return 'bg-green-600';
      case 'professional':
        return 'bg-purple-600';
      case 'business':
        return 'bg-orange-600';
      case 'pay_as_go':
        return 'bg-blue-600';
      case 'enterprise':
        return 'bg-gradient-to-r from-purple-600 to-pink-600';
      default:
        return 'bg-gray-600';
    }
  };
  const getPlanName = (planType: string) => {
    switch (planType) {
      case 'free':
        return 'Free';
      case 'starter':
        return 'Starter';
      case 'professional':
        return 'Professional';
      case 'business':
        return 'Business';
      case 'pay_as_go':
        return 'Pay as You Go';
      case 'enterprise':
        return 'Enterprise';
      default:
        return 'Free';
    }
  };
  return <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-hidden">
      {/* Background Effects - Optimized for performance */}
      <div className="absolute inset-0 hidden md:block">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:100px_100px]"></div>
        
        <div className="absolute inset-0 bg-grid-white/5 bg-[size:100px_100px] will-change-transform" style={{
        maskImage: `radial-gradient(500px at ${mousePosition.x}px ${mousePosition.y}px, transparent 0%, black 50%, transparent 90%)`,
        WebkitMaskImage: `radial-gradient(500px at ${mousePosition.x}px ${mousePosition.y}px, transparent 0%, black 50%, transparent 90%)`
      }} />
        
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/8 via-transparent to-purple-600/8"></div>
      </div>

      {/* Simplified mobile background - Static for better performance */}
      <div className="absolute inset-0 md:hidden bg-gradient-to-br from-blue-600/5 via-transparent to-purple-600/5"></div>

      {/* Animated background elements - Desktop only, reduced complexity */}
      <div className="hidden md:block absolute w-64 h-64 bg-blue-500/15 rounded-full blur-3xl will-change-transform" style={{
      top: '20%',
      left: '20%',
      animation: 'pulse 4s ease-in-out infinite'
    }}></div>
      <div className="hidden md:block absolute w-80 h-80 bg-purple-500/15 rounded-full blur-3xl will-change-transform" style={{
      bottom: '20%',
      right: '20%',
      animation: 'pulse 5s ease-in-out infinite'
    }}></div>

      {/* Header */}
      <header className="bg-black/20 backdrop-blur-xl border-b border-white/10 relative z-10 shadow-2xl">
        <div className="container mx-auto px-3 sm:px-4 md:px-6 py-2 sm:py-3 md:py-4">
          <nav className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2 md:space-x-3 group">
              <div className="w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl flex items-center justify-center shadow-lg group-hover:shadow-blue-500/25 transition-all duration-300 group-hover:scale-105">
                <span className="text-white font-bold text-xs sm:text-sm md:text-lg">BG</span>
              </div>
              <div className="flex flex-col">
                <span className="text-xs sm:text-sm md:text-lg font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                  <span className="inline sm:hidden">BG</span>
                  <span className="hidden sm:inline">The Bulk Image Generator</span>
                </span>
                <span className="text-[10px] sm:text-xs text-gray-300 hidden sm:block">From Ideas to Images, Instantly</span>
              </div>
            </Link>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-3">
              <Button onClick={handleHomeNavigation} variant="ghost" className="flex items-center text-white hover:bg-white/10 border border-white/20 hover:border-white/30 transition-all duration-200 hover:scale-105">
                <Home className="w-4 h-4 mr-2" />
                Home
              </Button>
              <Link to="/settings">
                <Button variant="ghost" className="flex items-center text-white hover:bg-white/10 border border-white/20 hover:border-white/30 transition-all duration-200 hover:scale-105">
                  <Settings className="w-4 h-4 mr-2" />
                  Settings
                </Button>
              </Link>
              <div className="flex items-center space-x-3 px-4 py-2 bg-white/10 rounded-xl border border-white/10 backdrop-blur-sm hover:bg-white/15 transition-all duration-200">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg flex items-center justify-center">
                  <User className="w-4 h-4 text-white" />
                </div>
                <div className="flex flex-col">
                  <span className="text-xs text-white font-medium truncate max-w-[120px]" title={user?.email}>
                    {user?.email?.split('@')[0] || 'User'}
                  </span>
                  <div className="flex items-center space-x-2">
                    {userData && !userDataLoading ? <div className="flex items-center space-x-2">
                        <Badge className={`${getPlanBadgeColor(userData.plan_type)} text-white text-xs px-2 py-1`}>
                          {getPlanName(userData.plan_type)}
                        </Badge>
                        {!canGenerateImages() && <span className="text-xs text-red-400">⚠️</span>}
                      </div> : <div className="flex items-center gap-2 bg-muted/20 rounded text-xs px-2 py-1">
                        <Loader2 className="w-3 h-3 animate-spin" />
                        Loading...
                      </div>}
                  </div>
                </div>
              </div>
              <Button variant="ghost" onClick={handleSignOut} className="text-red-600 border border-white/30 hover:border-white/50 transition-all duration-200 bg-white hover:bg-gray-100">
                Sign Out
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden">
              <Button variant="ghost" size="sm" onClick={() => setShowMobileMenu(!showMobileMenu)} className="text-white hover:bg-white/10 border border-white/20 p-2 h-10 w-10">
                <Menu className="w-5 h-5" />
              </Button>
            </div>
          </nav>

          {/* Mobile Menu */}
          {showMobileMenu && <div className="md:hidden mt-4 pb-4 border-t border-white/10 pt-4">
              <div className="space-y-3">
                <Button onClick={handleHomeNavigation} variant="ghost" className="w-full justify-start text-white hover:bg-white/10 border border-white/20 h-12">
                  <Home className="w-4 h-4 mr-3" />
                  Home
                </Button>
                <Link to="/settings" className="block">
                  <Button variant="ghost" className="w-full justify-start text-white hover:bg-white/10 border border-white/20 h-12">
                    <Settings className="w-4 h-4 mr-3" />
                    Settings
                  </Button>
                </Link>
                
                {/* Mobile User Info */}
                <div className="flex items-center space-x-3 px-4 py-3 bg-white/10 rounded-xl border border-white/10">
                  <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg flex items-center justify-center">
                    <User className="w-4 h-4 text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm text-white font-medium truncate" title={user?.email}>
                      {user?.email?.split('@')[0] || 'User'}
                    </div>
                    {userData && !userDataLoading && <div className="flex items-center space-x-2 mt-1">
                        <Badge className={`${getPlanBadgeColor(userData.plan_type)} text-white text-xs px-2 py-1`}>
                          {getPlanName(userData.plan_type)}
                        </Badge>
                        {!canGenerateImages() && <span className="text-xs text-red-400">⚠️</span>}
                      </div>}
                  </div>
                </div>

                <Button variant="ghost" onClick={handleSignOut} className="w-full justify-start text-red-600 border border-red-600/30 hover:border-red-600/50 bg-white hover:bg-gray-100 h-12">
                  Sign Out
                </Button>
              </div>
            </div>}
        </div>
      </header>

      <main className="container mx-auto px-3 sm:px-4 md:px-6 py-4 sm:py-6 md:py-8 relative z-10">
        {/* Hero Section */}
        <div className="mb-4 sm:mb-6 md:mb-8 lg:mb-12 text-center">
          <Badge className="mb-2 sm:mb-3 md:mb-4 bg-gradient-to-r from-blue-600/20 to-purple-600/20 text-blue-200 border-blue-500/30 px-2 md:px-3 py-1 text-[10px] sm:text-xs font-medium">
            ✨ Dashboard
          </Badge>
          <h1 className="text-lg sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl font-bold bg-gradient-to-r from-white via-blue-200 to-purple-200 bg-clip-text text-transparent mb-2 md:mb-3 animate-fade-in drop-shadow-lg">
            Welcome Back
          </h1>
          <p className="text-xs sm:text-sm md:text-base text-gray-100 max-w-2xl mx-auto leading-relaxed drop-shadow-sm px-2 sm:px-3">
            Generate stunning images with AI-powered creativity
          </p>
          {userData && <div className="mt-2 md:mt-3 inline-flex items-center gap-1.5 sm:gap-2 bg-gradient-to-r from-blue-600/20 to-purple-600/20 border border-blue-500/30 rounded-full px-2 sm:px-3 py-1 max-w-full">
              <span className="text-blue-300 text-[10px] sm:text-xs text-center break-words">
                🎯 {getPlanName(userData.plan_type)} Plan: {userData.monthly_limit === 999999 ? 'Unlimited' : userData.plan_type === 'pay_as_go' ? 'Pay per use' : `${Math.max(0, userData.monthly_limit - userData.image_generations_used)} left`}
              </span>
              {!canGenerateImages() && <span className="text-red-400 text-[10px] sm:text-xs font-medium whitespace-nowrap">
                  ⚠️ Limited
                </span>}
            </div>}
        </div>

        {/* Customer Readiness Notice for Admins */}
        <CustomerReadinessNotice />

        {/* Optimized Stats Grid */}
        <OptimizedDashboardStatsGrid />

        {/* Navigation - Mobile optimized */}
        <div className="mb-4 sm:mb-6 md:mb-8 lg:mb-10 flex justify-center">
          <div className="w-full overflow-x-auto md:overflow-visible">
            <NavBar items={navItems} activeTab={activeTab} onTabChange={handleTabChange} />
          </div>
        </div>

        {/* Main Content */}
        <div className="min-h-[300px] sm:min-h-[400px] md:min-h-[500px] relative">
          <div className="absolute inset-0 bg-gradient-to-br from-black/40 via-black/30 to-black/20 rounded-xl sm:rounded-2xl md:rounded-3xl border border-white/30 backdrop-blur-lg shadow-2xl"></div>
          
          <div className="relative z-10 p-3 sm:p-4 md:p-6 lg:p-10 rounded-xl sm:rounded-2xl md:rounded-3xl overflow-hidden bg-gradient-to-b from-white/5 to-transparent">
            <div className="absolute top-0 right-0 w-16 h-16 sm:w-24 sm:h-24 md:w-32 md:h-32 bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-full blur-xl"></div>
            <div className="absolute bottom-0 left-0 w-12 h-12 sm:w-16 sm:h-16 md:w-24 md:h-24 bg-gradient-to-tr from-cyan-500/10 to-pink-500/10 rounded-full blur-lg"></div>
            
            <div className="relative z-20 bg-gradient-to-br from-white/5 to-white/2 rounded-lg sm:rounded-xl md:rounded-2xl border border-white/20 backdrop-blur-sm p-3 sm:p-4 md:p-6 shadow-inner">
              {tabContent}
            </div>
          </div>
        </div>
      </main>

      {/* Plan Upgrade Modal */}
      <PlanUpgradeModal isOpen={showUpgradeModal} onClose={() => setShowUpgradeModal(false)} currentPlan={upgradeContext?.currentPlan || 'free'} requiredFeature={upgradeContext?.feature || ''} suggestedPlans={upgradeContext?.requiredPlans || []} />
      
      {/* Claimed Images Modal */}
      <ClaimedImagesModal 
        open={showClaimedModal} 
        onClose={() => setShowClaimedModal(false)} 
        images={claimedImages} 
      />
    </div>;
};
export default Dashboard;